import {
  Badge,
  Container,
  Flex,
  Divider,
  Group,
  Grid,
  List,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useFetch } from '@mantine/hooks';
import { useMemo } from 'react';
import PageHeader from '@components/shared/PageHeader';
import { Link, useParams } from 'react-router-dom';
import { MOCK_CERTIFICATE_TEMPLATES } from './mockCertificateTemplates';
import {
  ProCourseCertificateByTemplate,
  type ProCourseCertificateByTemplateProps,
} from '../certificateTemplates/certificateTemplateRegistry';
import { config } from '../config/env';
import {
  getCertificateTemplateId,
  resolveCertificateBranding,
} from '../certificateTemplates/courseCertificationDetails';
import type { ICourse } from '../interfaces/ICourse';
import { buildCertificateMetaLine } from '../utils/proCourseCertificateMeta';

type CourseWithCertificate = ICourse & { certificate?: string | null };

function getTemplateStatus(
  associatedCoursesCount: number
): 'active' | 'non active' {
  return associatedCoursesCount > 0 ? 'active' : 'non active';
}

function statusColor(status: 'active' | 'non active'): string {
  return status === 'active' ? 'teal' : 'gray';
}

function buildMockPreviewProps(
  template: (typeof MOCK_CERTIFICATE_TEMPLATES)[number]
): ProCourseCertificateByTemplateProps {
  if (template.templateId === 'cert2') {
    return {
      templateId: 'cert2',
      courseName: 'Neuroscienze e pratica clinica',
      courseHeldDateLabel: '24/03/2026',
      article: 'la',
      honorific: 'Dott.ssa',
      participantGivenName: 'Giulia',
      participantFamilyName: 'Rossi',
      fiscalCode: 'RSSGLI90A41F205X',
      participationDateLabel: '24/03/2026',
      ecmCreditsLabel: '12',
      professionLine: '5 Psicologo',
      disciplineLine: '77 Psicoterapia',
      issuerCityDateLabel: 'Milano, li 24/03/2026',
      eventNumber: '375106',
      editionNumber: '1',
    };
  }

  const mockCourseCertificateValue = template.templateId;
  const branding = resolveCertificateBranding(
    template.previewCourseId,
    mockCourseCertificateValue
  );
  const previewMetaCourse =
    template.templateId === 'cert1'
      ? { ecm: true, provider: 'Provider ECM: Spazio Iris' }
      : { ecm: true, provider: 'Ordine degli Psicologi della Lombardia' };
  return {
    templateId: template.templateId,
    courseName: 'Corso di competenza professionale',
    passedDateLabel: '24 marzo 2026',
    metaLine: buildCertificateMetaLine(previewMetaCourse),
    participantTitle: 'Dott.ssa',
    participantNameLine: 'Giulia Rossi',
    providerLogoUrl: null,
    signatureSlots: branding.signatureSlots,
    showIrisLogo: branding.showIrisLogo,
  };
}

export default function AdminCertificateTemplateDetail() {
  const { id } = useParams();
  const numericId = Number(id);
  const template = MOCK_CERTIFICATE_TEMPLATES.find(t => t.id === numericId);
  const previewProps = template ? buildMockPreviewProps(template) : null;
  const { data: allCoursesData } = useFetch<{ data: CourseWithCertificate[] }>(
    `${config.API_URL}/services/userCourses`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const associatedCourses = useMemo(() => {
    if (!template) return [];
    const fallbackNameById = new Map(
      template.associatedCourses.map(course => [course.id, course.name])
    );
    return (allCoursesData?.data ?? [])
      .filter(
        course =>
          getCertificateTemplateId(course.certificate) === template.templateId
      )
      .map(course => ({
        id: course.id,
        name:
          course.name ||
          fallbackNameById.get(course.id)?.replace(/^\d+\s*-\s*/, '') ||
          '',
      }));
  }, [allCoursesData, template]);
  const templateStatus = getTemplateStatus(associatedCourses.length);

  if (!template) {
    return (
      <Container size='2xl' py='lg'>
        <Stack gap='lg'>
          <PageHeader
            title='Certificate Template'
            breadcrumbs={[
              { label: 'Admin', href: '/admin' },
              {
                label: 'Certificate Templates',
                href: '/admin/courses/certificate-templates',
              },
              { label: 'Dettaglio' },
            ]}
            showBackButton={false}
          />
          <Paper withBorder p='md'>
            <Text c='red'>Template non trovato.</Text>
          </Paper>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size='2xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title='Certificate Template'
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            {
              label: 'Certificate Templates',
              href: '/admin/courses/certificate-templates',
            },
            { label: String(template.id) },
          ]}
          showBackButton={false}
        />

        <Grid gutter='lg'>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Paper withBorder p='md'>
              <Stack gap='sm'>
                <Group justify='space-between'>
                  <Title order={4}>{template.displayName}</Title>
                  <Badge color={statusColor(templateStatus)} variant='light'>
                    {templateStatus}
                  </Badge>
                </Group>
                <Text>
                  <strong>Template ID:</strong> {template.templateId}
                </Text>
                <Text>
                  <strong>Corsi associati:</strong> {associatedCourses.length}
                </Text>
                <Divider my='xs' />
                <Text fw={600}>Lista corsi associati:</Text>
                <List spacing='xs' size='sm'>
                  {associatedCourses.map(course => (
                    <List.Item key={course.id}>
                      <Text
                        component={Link}
                        to={`/proCourses/${course.id}`}
                        c='blue'
                      >
                        {`${course.id} - ${course.name}`}
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 8 }}>
            {previewProps ? (
              <Paper withBorder p='md'>
                <Stack gap='md'>
                  <Title order={5}>Anteprima template</Title>
                  <Flex justify='center' w='100%' style={{ overflow: 'auto' }}>
                    <ProCourseCertificateByTemplate {...previewProps} />
                  </Flex>
                </Stack>
              </Paper>
            ) : null}
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}
